import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { Link } from 'react-router-dom'
import { addApi } from 'shared/src/api'
import {
  addIntPathParam,
  addLocation,
  addNavigate,
  addPathParam,
} from 'shared/src/utils/addNavigate'
import { navBackOrHomepage } from 'shared/src/utils/navBack'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { trackUserListView } from 'shared/src/utils/analytics'
import { addAuthContext } from 'shared/src/utils/auth'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'

import backButtonIcon from 'assets/caret-left.svg'
import { ImageButton } from 'components/ImageButton'
import { ShareButton } from 'components/feed/ShareButton'
import { addLoadingAndErrorIndicators } from 'utils/addLoadingAndErrorIndicators'
import { ConstrainWidth, LAYOUT_MAX_WIDTH } from 'utils/layout'
import { makeCreatorEditsUrl, makeEditFeedUrl } from 'utils/routes'
import { serifFontFamily } from 'utils/styles'
import { NAVBAR_Z_INDEX } from 'utils/zIndex'
import { Edit } from './Edit'
import { Register } from './Register'

interface StyleProps {
  coverImageUrl?: string
}

const useStyles = createUseStyles({
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    gap: 9,
    height: ({ coverImageUrl }: StyleProps) => (coverImageUrl ? 288 : 'auto'),
    maxHeight: 288,
    minHeight: 200,
    background: ({ coverImageUrl }: StyleProps) =>
      coverImageUrl
        ? `linear-gradient(0deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.40) 100%), url(${coverImageUrl}) no-repeat center center/cover`
        : null,
  },
  title: {
    color: '#FFF',
    fontFamily: serifFontFamily,
    fontWeight: 800,
    fontStyle: 'italic',
    fontSize: 40,
    letterSpacing: '0.01em',
    textTransform: 'lowercase',
    paddingLeft: 40,
    paddingRight: 40,
    marginTop: 32,
    marginBottom: 32,
    textAlign: 'center',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  titleLink: {
    color: 'unset',
    textDecoration: 'none',
  },
  navbar: {
    display: 'flex',
    justifyContent: 'space-between',
    height: 51,
    zIndex: NAVBAR_Z_INDEX,
    position: 'absolute',
    width: '100%',
    maxWidth: LAYOUT_MAX_WIDTH,
    paddingTop: 6,
  },
  backButton: {
    color: 'white',
  },
  shareButton: {
    marginRight: 8,
  },
  blurredBackground: {
    '-webkit-filter': 'blur(6px)',
    '-moz-filter': 'blur(6px)',
    '-o-filter': 'blur(6px)',
    '-ms-filter': 'blur(6px)',
    filter: 'blur(6px)',
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'relative',
    height: '100dvh',
    widht: '100%',
  },
})

export const UserEdit: FC = flowMax(
  addDisplayName('UserEdit'),
  addApi,
  addPathParam('username'),
  addIntPathParam('editId'),
  addDataLoadingFromPromise(
    'list',
    ({ api, editId }) => api.userLists.getForPublic(editId),
    [],
    ({ editId }) => `user-list-${editId}`
  ),
  addLoadingAndErrorIndicators('list', { redirectTo404IfError: true }),
  addEffectOnMount(({ list, api }) => () => {
    trackUserListView(list.id, api)
  }),
  addNavigate,
  addLocation,
  addNavigationState('showBackButton', 'setShowBackButton', false),
  addHandlers({
    onClickBack:
      ({ navigate, username, location, showBackButton }) =>
      () => {
        if (showBackButton) {
          navBackOrHomepage()
        } else if (username) {
          navigate(makeCreatorEditsUrl(username), {
            state: {
              ...location.state,
            },
            replace: true,
          })
        }
      },
  }),
  addAuthContext,
  addProps(({ isAuthenticated }) => ({
    showRegister: !isAuthenticated,
  })),
  addProps(({ list: { edit } }) => ({
    classes: useStyles({ coverImageUrl: edit?.coverImage?.full }),
  })),
  ({ list, onClickBack, showRegister, classes }) => (
    <ConstrainWidth>
      <div className={classes.navbar}>
        <ImageButton
          image={backButtonIcon}
          width={20}
          height={20}
          onClick={onClickBack}
          className={classes.backButton}
        />
        <ShareButton
          context={{
            editId: list.edit?.id,
            creatorId: list.user.id,
          }}
          size={40}
          className={classes.shareButton}
        />
      </div>
      {showRegister && <Register />}
      <div className={cx(showRegister && classes.blurredBackground)}>
        <div className={classes.headerContainer}>
          {list.edit && list.edit.position !== null ? (
            <h1 className={classes.title}>
              <Link
                to={makeEditFeedUrl(list.edit.id)}
                className={classes.titleLink}
              >
                {list.edit.name}
              </Link>
            </h1>
          ) : (
            <h1 className={classes.title}>{list.edit?.name ?? list.name}</h1>
          )}
        </div>
        <Edit
          list={list}
          mode="user"
          isExpanded={true}
          onToggleExpand={() => {}}
        />
      </div>
    </ConstrainWidth>
  )
)
